import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { getFontSize } from "../../../../AppFont";
import {
  InitiativeWithProgressOutput,
  UnitEntity,
} from "../../../../generated/graphql";
import { InitiativePackageGroup, InitiativeWithPackageName } from "../../home2.hooks";
import { InitiativeListItem } from "../initiative-list-item/initiative-list-item";
import { ChevronRightIcon } from "@heroicons/react/outline";
import { MstrButton } from "../../../../components/mstr-button/mstr-button";
import { MstrButtonOutline } from "../../../../components/mstr-button-outline/mstr-button-outline";

import { CONSTANTS } from "../../../../constants";

const INITIAL_VISIBLE_COUNT = 5;

function getVisiblePackageGroups(
  groups: InitiativePackageGroup[],
  showAll: boolean,
  limit: number
): InitiativePackageGroup[] {
  if (showAll) return groups;

  let count = 0;
  const result: InitiativePackageGroup[] = [];
  for (const group of groups) {
    if (count >= limit) break;
    const remaining = limit - count;
    const visible = group.initiatives.slice(0, remaining);
    if (visible.length > 0) {
      result.push({ ...group, initiatives: visible });
      count += visible.length;
    }
  }
  return result;
}

function buildGroupsFromFlatList(
  items: InitiativeWithPackageName[]
): InitiativePackageGroup[] {
  const groups: InitiativePackageGroup[] = [];
  const groupIndexByName = new Map<string, number>();

  for (const { initiative, packageName } of items) {
    const key = packageName ?? "__ungrouped__";
    const existingIndex = groupIndexByName.get(key);

    if (existingIndex !== undefined) {
      groups[existingIndex].initiatives.push(initiative);
    } else {
      groupIndexByName.set(key, groups.length);
      groups.push({
        packageId: packageName ?? "0",
        packageName: packageName ?? "",
        initiatives: [initiative],
        isUngrouped: !packageName,
      });
    }
  }

  return groups;
}

interface ReassignInitiativeData {
  reassignInitiativeToLearner: UnitEntity;
}

const REASSIGN_INITIATIVE = gql`
  mutation ReassignInitiativeToLearner($initiative_id: Int!) {
    reassignInitiativeToLearner(initiative_id: $initiative_id) {
      id
      name
    }
  }
`;

interface InitiativeListCardProps {
  /** Initiatives grouped by package for sectioned display. */
  packageGroups?: InitiativePackageGroup[];
  /** Initiatives with optional package name for grouping display. If not provided, initiatives are shown without package badges. */
  initiativesWithPackage?: InitiativeWithPackageName[];
  /** @deprecated Use packageGroups or initiativesWithPackage. Flat list still supported for backward compatibility. */
  initiatives?: InitiativeWithProgressOutput[];
  refetchInitiatives?: () => void | Promise<unknown>;
}

export const InitiativeListCard = ({
  packageGroups,
  initiativesWithPackage,
  initiatives: initiativesProp,
  refetchInitiatives,
}: InitiativeListCardProps) => {
  const resolvedPackageGroups: InitiativePackageGroup[] | null = packageGroups
    ? packageGroups
    : initiativesWithPackage
      ? buildGroupsFromFlatList(initiativesWithPackage)
      : null;

  const initiatives = resolvedPackageGroups
    ? resolvedPackageGroups.flatMap((g) => g.initiatives)
    : initiativesProp ?? [];

  const hasPackageSections =
    resolvedPackageGroups !== null &&
    resolvedPackageGroups.some((g) => !g.isUngrouped);

  const [showAll, setShowAll] = useState(false);
  const [initiativeToReassign, setInitiativeToReassign] = useState<
    UnitEntity | undefined
  >(undefined);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToTop = () => {
    let el: HTMLElement | null = cardRef.current;
    while (el) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY || style.overflow;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight
      ) {
        el.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [reassignInitiative] = useMutation<ReassignInitiativeData>(
    REASSIGN_INITIATIVE,
    {
      update: () => {
        refetchInitiatives?.();
        setInitiativeToReassign(undefined);
      },
    }
  );

  const visiblePackageGroups = resolvedPackageGroups
    ? getVisiblePackageGroups(resolvedPackageGroups, showAll, INITIAL_VISIBLE_COUNT)
    : null;

  const visibleInitiatives = visiblePackageGroups
    ? visiblePackageGroups.flatMap((g) => g.initiatives)
    : showAll
      ? initiatives
      : initiatives.slice(0, INITIAL_VISIBLE_COUNT);

  const hasMore = initiatives.length > INITIAL_VISIBLE_COUNT;

  const handleToggleShowAll = () => {
    if (showAll) {
      setShowAll(false);
      scrollToTop();
    } else {
      setShowAll(true);
    }
  };

  const handleInitiativeClick = (initiativeWithProgress: InitiativeWithProgressOutput) => {
    const initiative = initiativeWithProgress.unit;
    const progress = Number(initiativeWithProgress.progress) ?? 0;
    if (progress >= 100) {
      setInitiativeToReassign(initiative);
    } else {
      const introShown = initiativeWithProgress.seen_intro;
      if (introShown || !initiative.intro) {
        navigate(`/initiative/${initiative.id}`);
      } else {
        navigate(`/initiative-intro/${initiative.id}`);
      }
    }
  };

  const reassignModal =
    initiativeToReassign &&
    createPortal(
      <div
        className="fixed inset-0 flex justify-center items-center"
        style={{
          zIndex: 9999,
          height: "calc(100vh - 64px)",
          left: 0,
          right: 0,
          bottom: 0,
          top: 64,
          background: "#ececec",
        }}
      >
        <div
          className="bg-white rounded-md shadow-lg"
          style={{ padding: 40, maxWidth: "90%" }}
        >
          <div style={{ fontSize: getFontSize() }}>
            Would you like to go through this initiative again?
          </div>
          <br />
          <br />
          <div className="flex gap-3">
            <MstrButton
              width={100}
              onClick={() => {
                reassignInitiative({
                  variables: {
                    initiative_id: +initiativeToReassign.id,
                  },
                });
              }}
              text="Yes"
            />
            <MstrButtonOutline
              width={100}
              onClick={() => setInitiativeToReassign(undefined)}
              text="No"
            />
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      {reassignModal}
      <div
        ref={cardRef}
        className="min-w-0 overflow-hidden bg-white rounded-xl shadow-sm border border-gray-100 pt-3.5 pb-4 pl-5 pr-4"
      >
        <h1 className="font-semibold">My Initiatives ({initiatives.length})</h1>
        {initiatives.length === 0 ? (
          <p className="mt-2 text-gray-500">No initiatives assigned yet.</p>
        ) : (
          <>
            <div className="mt-2 space-y-1 pt-2 -ml-2.5">
              {visiblePackageGroups ? (
                (() => {
                  let globalIndex = 0;
                  return visiblePackageGroups.map((group) => (
                    <div key={group.packageId} className="mb-3 last:mb-0">
                      {hasPackageSections && !group.isUngrouped && (
                        <div className="px-2 pb-1.5 pt-1">
                          <span
                            className="font-semibold text-gray-800"
                            style={{ fontSize: getFontSize() }}
                          >
                            {group.packageName}
                          </span>
                        </div>
                      )}
                      {hasPackageSections && group.isUngrouped && group.initiatives.length > 0 && (
                        <div
                          className="font-semibold text-gray-800 px-2 pb-1.5 pt-1"
                          style={{ fontSize: getFontSize() }}
                        >
                          {CONSTANTS.INITIATIVES_WITHOUT_PACKAGE_TITLE}
                        </div>
                      )}
                      {group.initiatives.map((initiativeWithProgress) => {
                        const itemIndex = globalIndex;
                        globalIndex += 1;
                        return (
                          <InitiativeListItem
                            index={itemIndex}
                            key={initiativeWithProgress.unit.id}
                            initiativeWithProgress={initiativeWithProgress}
                            onClick={() => handleInitiativeClick(initiativeWithProgress)}
                          />
                        );
                      })}
                    </div>
                  ));
                })()
              ) : (
                visibleInitiatives.map((initiativeWithProgress, index) => (
                  <InitiativeListItem
                    index={index}
                    key={initiativeWithProgress.unit.id}
                    initiativeWithProgress={initiativeWithProgress}
                    onClick={() => handleInitiativeClick(initiativeWithProgress)}
                  />
                ))
              )}
            </div>
            {hasMore && (
              <div
                className="flex justify-end items-center mt-2 pt-2 pr-0 -mr-1"
                style={{ color: "#014ec7" }}
              >
                <button
                  type="button"
                  onClick={handleToggleShowAll}
                  className="font-semibold"
                  style={{ fontSize: getFontSize() }}
                >
                  {showAll ? "See less" : "See all initiatives"}
                </button>
                <ChevronRightIcon
                  className={`h-4 w-4 ml-1 transition-transform ${showAll ? "rotate-90" : ""
                    }`}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
